package com.eladfinish.bible.contest.screens.chapterComplete.features.progressiveExposure

import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.common.usecases.PartitionChapterUseCase
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.ScrollEvent
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.model.ContentMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.CompleteType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.ImmutableMap
import kotlinx.collections.immutable.toImmutableList
import kotlinx.collections.immutable.toImmutableMap

/**
 * KMP replacement for legacy verseOrHalfOrWordCompletion().
 *
 * - Keeps progress inside state (ProgressiveExposureState)
 * - Rebuilds output text incrementally without Android TextView
 * - Returns updated parts map + a flag if we reached the end
 */
class VerseOrHalfOrWordCompletionUseCase(
    private val settings: KeyValueSettings,
    private val partitionChapterUseCase: PartitionChapterUseCase,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
) {

    private val eachVerseInNewLine: Boolean by lazy {
        settings.getBoolean(
            key = SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE,
            defaultValue = BooleanConstants.ENABLE_EACH_VERSE_IN_NEW_LINE,
        )
    }

    private val renderer: ExposureTextRenderer by lazy {
        ExposureTextRenderer(
            eachVerseInNewLine = eachVerseInNewLine,
            makaf = StringConstants.MAKAF,
        )
    }

    private val reducer: ExposureStepReducer by lazy {
        ExposureStepReducer(
            renderer = renderer,
        )
    }

    operator fun invoke(
        parts: ImmutableMap<Int, BrowseChaptersState.PartUiState>,
        currentPart: Int,
        completeType: CompleteType,
        chapterText: String,
        isFromSifreyEmet: Boolean,
    ): Output {

        val ready = parts[currentPart] as? BrowseChaptersState.PartUiState.Ready
            ?: return Output(
                parts = parts,
                reachedEnd = false,
            )

        val existingState = (ready.contentMode as? ContentMode.ProgressiveExposure)?.state

        val state = existingState ?: initState(
            chapterText = chapterText,
            isFromSifreyEmet = isFromSifreyEmet,
        )

        if (state.isFinished()) {
            return Output(
                parts = parts,
                reachedEnd = true,
            )
        }

        val updatedState = reducer.reduce(
            state = state,
            completeType = completeType,
        )

        val output = buildOutput(
            renderedText = updatedState.renderedTextSoFar,
        )

        val updatedReady = ready.copy(
            contentMode = ContentMode.ProgressiveExposure(
                state = updatedState,
                output = output,
            ),
            scrollEvent = ScrollEvent.ToBottom,
        )

        return Output(
            parts = parts
                .plus(
                    pair = currentPart to updatedReady,
                )
                .toImmutableMap(),
            reachedEnd = updatedState.isFinished(),
        )
    }

    data class Output(
        val parts: ImmutableMap<Int, BrowseChaptersState.PartUiState>,
        val reachedEnd: Boolean,
    )

    private fun initState(
        chapterText: String,
        isFromSifreyEmet: Boolean,
    ): ProgressiveExposureState {
        val partitioned = partitionChapterUseCase(
            chapter = chapterText,
            isFromSifreyEmet = isFromSifreyEmet,
        )

        val verseStartSegments = buildVerseStartSegments(
            partitioned = partitioned,
        )

        return ProgressiveExposureState(
            partitionedChapter = partitioned,
            verseStartSegments = verseStartSegments,
            currentSegment = 0,
            currentWord = 0,
            elementsNumber = verseStartSegments.lastOrNull() ?: 0,
            renderedTextSoFar = "",
        )
    }

    private fun buildVerseStartSegments(
        partitioned: List<List<List<String>>>,
    ): ImmutableList<Int> {
        val starts = mutableListOf<Int>()

        var acc = 0
        starts += 0

        partitioned.forEach { verseParts ->
            // verseParts[0] is otPasuk; the rest are the actual verse parts (2 or 3 in sifrey Emet).
            val segmentsInVerse = (verseParts.size - 1).coerceAtLeast(1)
            acc += segmentsInVerse
            starts += acc
        }

        return starts.toImmutableList()
    }

    private fun buildOutput(
        renderedText: String,
    ): SpannableStringKMP {
        // Shrink ot pasuk letters, like legacy makeOtiotPasukSmaller().
        val otPasukSpans: List<SpannedRange> = makeOtiotPasukSmallerUseCase(
            charSequence = renderedText,
        )

        return SpannableStringKMP(
            charSequence = renderedText,
            spannedRangeList = otPasukSpans.toImmutableList(),
        )
    }
}
